import { differenceInCalendarDays } from 'date-fns';

const cleanString = (str) => {
  if (!str) return '';
  return str.replace(/^\uFEFF/, '').replace(/^["']|["']$/g, '').trim();
};

const splitCSVLine = (line, separator) => {
  const result = [];
  let start = 0;
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    if (line[i] === '"') inQuotes = !inQuotes;
    if (!inQuotes && line[i] === separator) {
      result.push(line.substring(start, i));
      start = i + 1;
    }
  }
  result.push(line.substring(start));
  return result.map(v => cleanString(v));
};

export const parseLexwareCSV = (csvText) => {
  const lines = csvText.split(/\r?\n/).filter(line => line.trim());
  if (lines.length < 2) return [];
  const separator = lines[0].includes(';') ? ';' : '\t';
  const headers = splitCSVLine(lines[0], separator);
  const result = [];

  for (let i = 1; i < lines.length; i++) {
    const values = splitCSVLine(lines[i], separator);
    const entry = {};
    headers.forEach((header, index) => {
      let value = values[index];
      if (['Steuerbetrag', 'Gesamtbetrag'].includes(header) && value) {
        value = parseFloat(value.replace(/\./g, '').replace(',', '.')) || 0;
      }
      if (['RG-Datum', 'Liefer-Datum', 'Fällig', 'bezahlt am'].includes(header) && value) {
        const dateMatch = value.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
        if (dateMatch) {
          value = `${dateMatch[3]}-${dateMatch[2].padStart(2, '0')}-${dateMatch[1].padStart(2, '0')}`;
        }
      }
      entry[header] = value;
    });
    const brutto = entry['Gesamtbetrag'] || 0;
    const steuer = entry['Steuerbetrag'] || (brutto * 0.19 / 1.19);
    entry._netto = brutto - steuer;
    if (entry['Kundenummer']) result.push(entry);
  }
  return result;
};

export const parseCustomerImportCSV = (csvText) => {
  const lines = csvText.split(/\r?\n/).filter(line => line.trim());
  if (lines.length < 2) return { customers: {}, contacts: [] };
  const separator = lines[0].includes(';') ? ';' : '\t';
  const headers = splitCSVLine(lines[0], separator);

  const customers = {};
  const contacts = [];

  for (let i = 1; i < lines.length; i++) {
    const v = splitCSVLine(lines[i], separator);
    const get = (h) => v[headers.indexOf(h)] || '';
    
    const kdnr = get('Kundennummer');
    if (!kdnr) continue;

    customers[kdnr] = {
      id: kdnr,
      name: get('Firmenname'),
      email: get('E-Mail 1'),
      phone: get('Telefon 1'),
      address: get('Straße 1'),
      zip: get('PLZ 1'),
      city: get('Ort 1')
    };

    const processAP = (n) => {
      const nachname = get(`Ansprechpartner ${n} Nachname`);
      const vorname = get(`Ansprechpartner ${n} Vorname`);
      const anredeCSV = get(`Ansprechpartner ${n} Anrede`); // Lexware Feld
      
      if (nachname) {
        // Mapping der CSV-Anrede auf unser System
        let detectedGender = 'n';
        const rawAnrede = anredeCSV.toLowerCase();
        if (rawAnrede.includes('herr')) detectedGender = 'm';
        else if (rawAnrede.includes('frau')) detectedGender = 'f';

        contacts.push({
          customerId: kdnr,
          name: `${vorname} ${nachname}`.trim(),
          email: get(`Ansprechpartner ${n} E-Mail`),
          phone: get(`Ansprechpartner ${n} Telefon`),
          role: get(`Ansprechpartner ${n} Abteilung`) || `AP ${n}`,
          gender: detectedGender,
          useDu: false, // Standard: Sie
          greetingType: 'formal' // Standard: Sehr geehrte/r
        });
      }
    };
    processAP(1);
    processAP(2);
  }
  return { customers, contacts };
};

export const extractCustomersFromSales = (sales, masterData = {}) => {
  const map = new Map();
  sales.forEach(s => {
    const id = s['Kundenummer'];
    if (!id) return;
    if (!map.has(id)) {
      map.set(id, { id, name: s['Firmenname'], totalValue: 0, _dates: [] });
    }
    const c = map.get(id);
    c.totalValue += (s._netto || 0);
    if (s['RG-Datum']) c._dates.push(new Date(s['RG-Datum']));
  });

  const today = new Date();

  return Array.from(map.values()).map(c => {
    const m = masterData[c.id] || {};
    c._dates.sort((a, b) => b - a);
    
    const lastOrderDate = c._dates.length > 0 ? c._dates[0] : null;
    const overdueDays = lastOrderDate ? differenceInCalendarDays(today, lastOrderDate) : 0;
    
    return { 
      ...c, 
      ...m, 
      lastOrderDate, 
      overdueDays,
      daysSinceLastOrder: overdueDays, // Alias für Kompatibilität
      avgGapDays: 30 
    };
  });
};